/**
 * Generation Step Feedback Service
 *
 * Expert-only per-step feedback on generation log entries
 * (thumbs up/down + optional note). Upsert semantics per
 * (step_index, author) on the backend — re-submitting updates.
 *
 * See also:
 * - ./blogCreationService.ts (getGenerationTimeline)
 * - ../hooks/useGenerationTimeline.ts (React Query hooks)
 */

import { apiClient } from './apiClient';
import { useTenantStore } from '../stores/useTenantStore';
import type { StepFeedback } from '../components/BlogCreationCommandCenter/types';

const API_BASE = '/blog-creation';

// No test-tenant fallback (issue #186): a race / cleared localStorage must
// never silently route expert feedback to the wrong tenant. The React Query
// hooks additionally gate `enabled` on a non-empty tenant so this throw is
// a last-resort guard, not an expected path.
const getTenantId = (): string => {
  const tenant = useTenantStore.getState().currentTenant;
  if (!tenant) {
    throw new Error('No tenant selected — cannot submit generation feedback');
  }
  return tenant;
};

export interface SubmitStepFeedbackData {
  step_index: number;
  step_tool?: string;
  step_ts?: string;
  verdict: 'up' | 'down';
  /** Critique of the step (free-form) */
  note?: string;
  /** Training exemplar: what the step should have produced instead */
  expected_output?: string;
}

export const generationFeedbackService = {
  // List all feedback rows for a content item
  async list(contentId: string, signal?: AbortSignal): Promise<{ items: StepFeedback[] }> {
    const response = await apiClient.get<{ items: StepFeedback[] }>(
      `${API_BASE}/content/${contentId}/step-feedback?tenant_id=${getTenantId()}`,
      { signal }
    );
    return response.data;
  },

  // Submit (upsert) feedback for a step — 201 returns the row
  async submit(contentId: string, data: SubmitStepFeedbackData): Promise<StepFeedback> {
    const response = await apiClient.post<StepFeedback>(
      `${API_BASE}/content/${contentId}/step-feedback?tenant_id=${getTenantId()}`,
      data
    );
    return response.data;
  },

  // Delete a feedback row
  async remove(contentId: string, feedbackId: string): Promise<void> {
    await apiClient.delete(
      `${API_BASE}/content/${contentId}/step-feedback/${feedbackId}?tenant_id=${getTenantId()}`
    );
  },
};
